import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useParams, Link } from "react-router-dom";
import { FaTrash, FaPencilAlt, FaClipboardCheck } from "react-icons/fa";
import axios from "../../axiosConfig";
import { useSurveyPages, SurveyPagesProvider } from "../../contexts/SurveyPagesContext";
import { getAreaName } from "../../utils/areaNames";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

const NAME_MAX_CHARS = 100;
const FACTOR_MAX_CHARS = 80;

const SurveyPageListContent = () => {
  const { surveyId } = useParams();
  const { pages, fetchPages, deletePage } = useSurveyPages();
  const [survey, setSurvey] = useState(null);
  const [loadingSurvey, setLoadingSurvey] = useState(true);

  const isMobile = useBreakpointValue({ base: true, md: false });

  // ✅ Sorting
  const [sortKey, setSortKey] = useState("position"); // name | position | type | area | factor
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  // Fetch survey details for header
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const response = await axios.get(`/surveys/${surveyId}`);
        setSurvey(response.data);
      } catch (error) {
        console.error("Error fetching survey:", error);
      } finally {
        setLoadingSurvey(false);
      }
    };

    fetchSurvey();
  }, [surveyId]);

  // Fetch pages initially
  useEffect(() => {
    fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  /* ---------------- sorting helpers ---------------- */

  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const pageTypeLabel = (page) => (page.extra_team_only ? "extra teams" : "main");
  const areaLabel = (page) => normalize(getAreaName(page.area) || "");

  const sortedPages = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    const valueFor = (p) => {
      switch (sortKey) {
        case "name":
          return normalize(p.name);
        case "position":
          return Number(p.position ?? 0);
        case "type":
          return pageTypeLabel(p);
        case "area":
          return areaLabel(p);
        case "factor":
          return normalize(p.factor);
        default:
          return normalize(p.name);
      }
    };

    return [...(pages ?? [])].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);

      if (typeof av === "number" && typeof bv === "number") {
        if (av !== bv) return (av - bv) * dir;
      } else {
        const cmp = av.localeCompare(bv);
        if (cmp !== 0) return cmp * dir;
      }

      // stable-ish fallback: position then name
      const posCmp = Number(a.position ?? 0) - Number(b.position ?? 0);
      if (posCmp !== 0) return posCmp;
      return normalize(a.name).localeCompare(normalize(b.name));
    });
  }, [pages, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIndicator = ({ active }) => {
    if (!active) return null;
    return sortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  /* ---------------- render helpers ---------------- */

  const renderCard = (page) => (
    <Box
      key={page.id}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      bg={cardBg}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <HStack justify="space-between" w="100%" align="start">
          <Box minW={0}>
            <TruncatedText
              value={page.name}
              maxChars={NAME_MAX_CHARS}
              fontWeight="bold"
              fontSize="md"
            />
            <Text fontSize="sm" color={subTextColor}>
              Position: {page.position ?? "—"}
            </Text>
          </Box>

          <Badge
            variant="subtle"
            colorScheme={page.extra_team_only ? "purple" : "blue"}
            borderRadius="md"
          >
            {page.extra_team_only ? "Extra teams" : "Main"}
          </Badge>
        </HStack>

        <HStack spacing={6} flexWrap="wrap">
          <Box minW={0}>
            <Text fontSize="sm" color={subTextColor}>
              Area
            </Text>
            <TruncatedText value={getAreaName(page.area) || "—"} maxChars={50} tooltip={false} />
          </Box>

          <Box minW={0}>
            <Text fontSize="sm" color={subTextColor}>
              Factor
            </Text>
            <TruncatedText value={page.factor || "—"} maxChars={FACTOR_MAX_CHARS} />
          </Box>
        </HStack>

        <HStack w="100%" justify="flex-end" spacing={2} flexWrap="wrap">
          <Tooltip label="Manage questions" hasArrow>
            <IconButton
              icon={<FaClipboardCheck />}
              colorScheme="cyan"
              aria-label="Manage Questions"
              title="Manage Questions"
              variant="outline"
              as={Link}
              size="sm"
              to={`/app/surveys/${surveyId}/pages/${page.id}/questions`}
            />
          </Tooltip>

          <Tooltip label="Edit page" hasArrow>
            <IconButton
              icon={<FaPencilAlt />}
              colorScheme="yellow"
              aria-label="Edit Page"
              title="Edit Page"
              variant="outline"
              as={Link}
              size="sm"
              to={`/app/surveys/${surveyId}/pages/edit/${page.id}`}
            />
          </Tooltip>

          <Tooltip label="Delete page" hasArrow>
            <IconButton
              icon={<FaTrash />}
              aria-label="Delete Page"
              title="Delete Page"
              variant="outline"
              colorScheme="red"
              size="sm"
              onClick={() => deletePage(page.id)}
            />
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );

  const renderTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "32%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>

          <Thead>
            <Tr
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: cardBg,
              }}
            >
              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("name")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Page name <SortIndicator active={sortKey === "name"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("position")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                isNumeric
              >
                Position <SortIndicator active={sortKey === "position"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("type")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Type <SortIndicator active={sortKey === "type"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("area")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Area <SortIndicator active={sortKey === "area"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("factor")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Factor <SortIndicator active={sortKey === "factor"} />
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {sortedPages.map((page) => (
              <Tr key={page.id}>
                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText value={page.name} maxChars={NAME_MAX_CHARS} fontWeight="semibold" />
                  </Box>
                </Td>

                <Td py={3} isNumeric fontVariantNumeric="tabular-nums">
                  {page.position ?? "—"}
                </Td>

                <Td py={3}>
                  <Badge
                    variant="subtle"
                    colorScheme={page.extra_team_only ? "purple" : "blue"}
                    borderRadius="md"
                  >
                    {page.extra_team_only ? "Extra teams" : "Main"}
                  </Badge>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText value={getAreaName(page.area) || "—"} maxChars={60} tooltip={false} />
                  </Box>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText value={page.factor || "—"} maxChars={FACTOR_MAX_CHARS} />
                  </Box>
                </Td>

                <Td py={3}>
                  <HStack spacing={2}>
                    <Tooltip label="Manage questions" hasArrow>
                      <IconButton
                        icon={<FaClipboardCheck />}
                        colorScheme="cyan"
                        aria-label="Manage Questions"
                        title="Manage Questions"
                        variant="outline"
                        as={Link}
                        size="sm"
                        to={`/app/surveys/${surveyId}/pages/${page.id}/questions`}
                      />
                    </Tooltip>

                    <Tooltip label="Edit page" hasArrow>
                      <IconButton
                        icon={<FaPencilAlt />}
                        colorScheme="yellow"
                        aria-label="Edit Page"
                        title="Edit Page"
                        variant="outline"
                        as={Link}
                        size="sm"
                        to={`/app/surveys/${surveyId}/pages/edit/${page.id}`}
                      />
                    </Tooltip>

                    <Tooltip label="Delete page" hasArrow>
                      <IconButton
                        icon={<FaTrash />}
                        aria-label="Delete Page"
                        title="Delete Page"
                        variant="outline"
                        colorScheme="red"
                        size="sm"
                        onClick={() => deletePage(page.id)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  const actions = (
    <HStack spacing={3} flexWrap="wrap">
      {isMobile && (
        <Select
          size="sm"
          w="240px"
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(":");
            setSortKey(k);
            setSortDir(d);
          }}
        >
          <option value="position:asc">Position (Low–High)</option>
          <option value="position:desc">Position (High–Low)</option>
          <option value="name:asc">Page name (A–Z)</option>
          <option value="name:desc">Page name (Z–A)</option>
          <option value="type:asc">Type (A–Z)</option>
          <option value="type:desc">Type (Z–A)</option>
          <option value="area:asc">Area (A–Z)</option>
          <option value="area:desc">Area (Z–A)</option>
          <option value="factor:asc">Factor (A–Z)</option>
          <option value="factor:desc">Factor (Z–A)</option>
        </Select>
      )}

      <Button
        colorScheme="green"
        variant="outline"
        size="sm"
        as={Link}
        to={`/app/surveys/${surveyId}/pages/create`}
      >
        Add Page
      </Button>
    </HStack>
  );

  const title = loadingSurvey ? "Survey" : survey?.name || "Survey";
  const subtitle = "Manage Pages";

  return (
    <ListPageLayout title={title} actions={actions} contentProps={{ maxW: "100%", minW: 0 }}>
      <Text fontSize="md" color={subTextColor} mb={3}>
        {subtitle}
      </Text>

      {pages?.length > 0 ? (
        isMobile ? (
          <VStack spacing={4} align="stretch">
            {sortedPages.map(renderCard)}
          </VStack>
        ) : (
          renderTable()
        )
      ) : (
        <Text mt={10} textAlign="center">
          No pages found for this survey.
        </Text>
      )}
    </ListPageLayout>
  );
};

// Wrapping SurveyPageListContent in SurveyPagesProvider to access survey pages
const SurveyPageList = () => {
  const { surveyId } = useParams();

  return (
    <SurveyPagesProvider surveyId={surveyId}>
      <SurveyPageListContent />
    </SurveyPagesProvider>
  );
};

export default SurveyPageList;